import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  Settings,
  Bell,
  LogOut,
  Diamond,
  Crown,
  UserCog,
  Code2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/resellers", icon: Users, label: "Resellers" },
  { to: "/admin/recharges", icon: ListChecks, label: "All Recharges" },
  { to: "/admin/noble-packs", icon: Crown, label: "Noble Packs" },
  { to: "/admin/api-docs", icon: Code2, label: "API Docs" },
  { to: "/admin/settings", icon: Settings, label: "Session Cookie" },
  { to: "/admin/alerts", icon: Bell, label: "Alerts" },
  { to: "/admin/account", icon: UserCog, label: "Account" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-40 w-64 bg-zinc-950 text-zinc-300 flex-col">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-zinc-800">
          <Diamond className="w-5 h-5 text-white" strokeWidth={1.5} />
          <div>
            <div className="font-display text-sm font-semibold text-white leading-none">Shinning Eyes</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Admin</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-800 text-white font-medium"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-500">Signed in as</div>
            <div className="text-sm text-white font-medium truncate" data-testid="admin-user-email">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between border-b border-zinc-200 bg-white/80 backdrop-blur-md px-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Admin</div>
            <div className="font-display text-sm font-semibold text-zinc-900">Control Console</div>
          </div>
          <button
            onClick={handleLogout}
            className="md:hidden text-sm text-zinc-600 hover:text-zinc-900"
            data-testid="admin-logout-button-mobile"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </header>
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
